import intersection from "lodash/intersection";
import isEmpty from "lodash/isEmpty";
import { isAudit, isEngagement, isRole } from "PFApp/activities/helpers";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplateFind } from "PFApp/hooks";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useCallback } from "react";

import { getAvailableBookingCategoryValues } from "./use_available_booking_categories.utils";

type ActivityData = {
  activityTemplateId: number | undefined;
  activitySubtemplateKey: string | undefined;
  parentActivitySubtemplateKey?: string | undefined;
};
type UseAvailableBookingCategoriesReturn = ({ activityData }: { activityData: ActivityData[] }) => number[];

export const useAvailableBookingCategories = (): UseAvailableBookingCategoriesReturn => {
  const findTemplate = useTemplateFind();
  const { bookingCategories } = useBookingCategories();

  return useCallback<UseAvailableBookingCategoriesReturn>(
    ({ activityData }) => {
      if (isEmpty(activityData)) {
        return bookingCategories.map(({ id }) => id);
      }
      const bookingCategoryIds = activityData.map(
        ({ activityTemplateId, activitySubtemplateKey, parentActivitySubtemplateKey }) => {
          const activityTemplate = findTemplate({ id: activityTemplateId });
          const isActivityAudit = isAudit(activityTemplate);
          const engagementTemplate = findTemplate({
            key: isActivityAudit ? TemplateKey.AuditEngagement : TemplateKey.Engagement
          });

          if (!activityTemplate) {
            return bookingCategories.map(({ id }) => id);
          }
          const isActivityRole = isRole(activityTemplate);
          const isActivityEngagement = isEngagement(activityTemplate);

          const availableBookingCategoryIds = getAvailableBookingCategoryValues({
            isRole: isActivityRole,
            isEngagement: isActivityEngagement,
            engagementSchema: engagementTemplate?.schema,
            parentActivitySubtemplateKey,
            subtemplateKey: activitySubtemplateKey,
            template: activityTemplate
          });

          if (isEmpty(availableBookingCategoryIds)) {
            return bookingCategories.map(({ id }) => id);
          }

          return availableBookingCategoryIds;
        }
      );
      return intersection(...bookingCategoryIds);
    },
    [bookingCategories, findTemplate]
  );
};
